import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Card, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, Chip, IconButton, Tooltip,
    Button, TextField, MenuItem, Grid, Alert, CircularProgress,
    Drawer, Divider, Stack, Autocomplete, Paper,
} from '@mui/material';
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import eyeFill from '@iconify/icons-eva/eye-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import personFill from '@iconify/icons-eva/person-fill';
import personAddFill from '@iconify/icons-eva/person-add-fill';
import { LoadingButton } from '@mui/lab';
import { adminSyndicationApi } from '../../../../utils/syndicationApi';

const STATUS_COLORS = {
    pending_email_verification: 'warning',
    active: 'success',
    suspended: 'error',
};

const ADD_NEW_SENTINEL = { id: '__ADD_NEW__', first_name: '+ Add New Member', last_name: '', email: '' };

export default function AdminFounderList() {
    const [founders, setFounders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Create drawer
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({
        first_name: '', last_name: '', email: '', phone: '',
        company_id: '', linkedin_url: '', bio: '', deal_ids: [],
    });
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');
    const [createSuccess, setCreateSuccess] = useState('');

    // Detail drawer
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedFounder, setSelectedFounder] = useState(null);
    const [resetting2FA, setResetting2FA] = useState(false);

    // Companies + Deals for dropdowns (searchable)
    const [companies, setCompanies] = useState([]);
    const [companyLoading, setCompanyLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [deals, setDeals] = useState([]);
    const [dealLoading, setDealLoading] = useState(false);
    const [selectedDeals, setSelectedDeals] = useState([]);

    // Company members for member selection
    const [companyMembers, setCompanyMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [isNewMember, setIsNewMember] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchFounders();
    }, []);

    const fetchFounders = async () => {
        setLoading(true);
        try {
            const res = await adminSyndicationApi.get('admin/founders/');
            setFounders(res.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load founders.');
        } finally {
            setLoading(false);
        }
    };

    const companyDebounceRef = useRef(null);
    const dealDebounceRef = useRef(null);

    const fetchCompanies = (search = '') => {
        setCompanyLoading(true);
        const q = search ? `admin/company-lookup/?page_size=10&search=${encodeURIComponent(search)}` : 'admin/company-lookup/?page_size=10';
        adminSyndicationApi.get(q)
            .then(res => setCompanies(res.data?.results || []))
            .catch(() => {})
            .finally(() => setCompanyLoading(false));
    };

    const debouncedFetchCompanies = (val) => {
        if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);
        companyDebounceRef.current = setTimeout(() => fetchCompanies(val), 400);
    };

    const fetchCompanyMembers = async (companyId) => {
        setMembersLoading(true);
        try {
            const res = await adminSyndicationApi.get(`admin/company-members/?company_id=${companyId}`);
            setCompanyMembers(res.data?.results || []);
        } catch {
            setCompanyMembers([]);
        } finally {
            setMembersLoading(false);
        }
    };

    const fetchDeals = (search = '') => {
        setDealLoading(true);
        const q = search ? `admin/deals/?search=${encodeURIComponent(search)}` : 'admin/deals/';
        adminSyndicationApi.get(q)
            .then(res => setDeals(res.data?.data || []))
            .catch(() => {})
            .finally(() => setDealLoading(false));
    };

    const debouncedFetchDeals = (val) => {
        if (dealDebounceRef.current) clearTimeout(dealDebounceRef.current);
        dealDebounceRef.current = setTimeout(() => fetchDeals(val), 400);
    };

    const handleCompanyChange = (val) => {
        setSelectedCompany(val);
        setCreateForm(p => ({
            ...p,
            company_id: val?.id || '',
            first_name: '', last_name: '', email: '', phone: '', linkedin_url: '',
        }));
        setSelectedMember(null);
        setIsNewMember(false);
        setCompanyMembers([]);
        if (val?.id) {
            fetchCompanyMembers(val.id);
        }
    };

    const handleMemberSelect = (val) => {
        if (!val) {
            setSelectedMember(null);
            setIsNewMember(false);
            setCreateForm(p => ({ ...p, first_name: '', last_name: '', email: '', phone: '', linkedin_url: '' }));
            return;
        }
        if (val.id === '__ADD_NEW__') {
            setSelectedMember(null);
            setIsNewMember(true);
            setCreateForm(p => ({ ...p, first_name: '', last_name: '', email: '', phone: '', linkedin_url: '' }));
            return;
        }
        setSelectedMember(val);
        setIsNewMember(false);
        setCreateForm(p => ({
            ...p,
            first_name: val.first_name || '',
            last_name: val.last_name || '',
            email: val.email || '',
            phone: val.phone || '',
            linkedin_url: val.linkedin_url || '',
        }));
    };

    const resetCreateForm = () => {
        setCreateForm({
            first_name: '', last_name: '', email: '', phone: '',
            company_id: '', linkedin_url: '', bio: '', deal_ids: [],
        });
        setSelectedCompany(null);
        setSelectedMember(null);
        setIsNewMember(false);
        setSelectedDeals([]);
        setCompanyMembers([]);
        setCreateError('');
        setCreateSuccess('');
    };

    const handleCreate = async () => {
        setCreating(true);
        setCreateError('');
        setCreateSuccess('');
        try {
            const payload = {
                ...createForm,
                company_id: Number(createForm.company_id),
                deal_ids: createForm.deal_ids.map(Number),
            };
            await adminSyndicationApi.post('admin/founders/', payload);
            setCreateSuccess('Founder created. Welcome email sent.');
            resetCreateForm();
            fetchFounders();
        } catch (err) {
            const data = err.response?.data;
            const msg = typeof data === 'object'
                ? Object.values(data).flat().join(', ')
                : data?.message || 'Create failed.';
            setCreateError(msg);
        } finally {
            setCreating(false);
        }
    };

    const handleViewDetail = async (founder) => {
        try {
            const res = await adminSyndicationApi.get(`admin/founders/${founder.id}/`);
            setSelectedFounder(res.data?.data);
            setDetailOpen(true);
        } catch (err) {
            alert('Failed to load founder details.');
        }
    };

    const handleReset2FA = async () => {
        if (!selectedFounder) return;
        setResetting2FA(true);
        try {
            await adminSyndicationApi.post(`admin/founders/${selectedFounder.id}/reset-2fa/`);
            alert('2FA reset. Setup email sent to founder.');
            handleViewDetail(selectedFounder);
        } catch (err) {
            alert(err.response?.data?.message || 'Reset failed.');
        } finally {
            setResetting2FA(false);
        }
    };

    const paginatedFounders = founders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Show form fields when: existing member selected OR "Add New" chosen
    const showFormFields = selectedMember || isNewMember;
    // Fields are editable only for new members (existing member pre-fills but locked for name/email)
    const memberLocked = !!selectedMember && !isNewMember;

    // Build member options: existing members + "Add New" sentinel at the end
    const memberOptions = [...companyMembers, ADD_NEW_SENTINEL];

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>Syndication Founders</Typography>
                <Button
                    variant="contained" startIcon={<Icon icon={plusFill} />}
                    onClick={() => { setCreateOpen(true); resetCreateForm(); }}
                    sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d1642' } }}
                >
                    Add Founder
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Card>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>2FA</TableCell>
                                <TableCell>Deals</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : paginatedFounders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">No founders yet.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : paginatedFounders.map((f) => (
                                <TableRow key={f.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                            {f.first_name} {f.last_name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{f.email}</TableCell>
                                    <TableCell>{f.company_name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={f.status?.replace(/_/g, ' ')}
                                            color={STATUS_COLORS[f.status] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={f.totp_enabled ? 'Enabled' : 'Disabled'}
                                            color={f.totp_enabled ? 'success' : 'default'} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>{f.deal_count || 0}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View Details">
                                            <IconButton size="small" onClick={() => handleViewDetail(f)}>
                                                <Icon icon={eyeFill} width={20} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div" count={founders.length}
                    page={page} onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Card>

            {/* Create Founder Drawer */}
            <Drawer anchor="right" open={createOpen} onClose={() => setCreateOpen(false)}
                PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight={700}>Add Founder</Typography>
                        <IconButton onClick={() => setCreateOpen(false)}><Icon icon={closeFill} width={24} /></IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                        {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
                        {createSuccess && <Alert severity="success" sx={{ mb: 2 }}>{createSuccess}</Alert>}
                        <Stack spacing={2.5}>
                            {/* STEP 1: Select Company */}
                            <Box>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
                                    Step 1 — Select Company
                                </Typography>
                                <Autocomplete
                                    options={companies}
                                    getOptionLabel={(opt) => opt.company_name || ''}
                                    value={selectedCompany}
                                    loading={companyLoading}
                                    onOpen={() => { if (companies.length === 0) fetchCompanies(); }}
                                    onInputChange={(e, val, reason) => {
                                        if (reason === 'input') debouncedFetchCompanies(val);
                                    }}
                                    onChange={(e, val) => handleCompanyChange(val)}
                                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth label="Company *"
                                            placeholder="Search companies..."
                                            InputProps={{ ...params.InputProps, endAdornment: (
                                                <>{companyLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>
                                            )}}
                                        />
                                    )}
                                    filterOptions={(x) => x}
                                    noOptionsText="Type to search..."
                                />
                            </Box>

                            {/* STEP 2: Select Existing Member or Add New */}
                            {selectedCompany && (
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
                                        Step 2 — Select Member
                                    </Typography>
                                    {membersLoading ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                                            <CircularProgress size={18} />
                                            <Typography variant="body2" color="text.secondary">Loading members...</Typography>
                                        </Box>
                                    ) : (
                                        <Autocomplete
                                            options={memberOptions}
                                            getOptionLabel={(opt) => {
                                                if (opt.id === '__ADD_NEW__') return '+ Add New Member';
                                                const name = `${opt.first_name} ${opt.last_name}`.trim();
                                                return opt.email ? `${name} (${opt.email})` : name;
                                            }}
                                            value={isNewMember ? ADD_NEW_SENTINEL : selectedMember}
                                            onChange={(e, val) => handleMemberSelect(val)}
                                            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                            renderOption={(props, opt) => {
                                                if (opt.id === '__ADD_NEW__') {
                                                    return (
                                                        <li {...props} key="__ADD_NEW__">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontWeight: 600 }}>
                                                                <Icon icon={personAddFill} width={18} />
                                                                <span>Add New Member</span>
                                                            </Box>
                                                        </li>
                                                    );
                                                }
                                                const name = `${opt.first_name} ${opt.last_name}`.trim();
                                                return (
                                                    <li {...props} key={opt.id}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                            <Icon icon={personFill} width={18} color="#637381" />
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body2" fontWeight={600}>{name}</Typography>
                                                                {opt.email && <Typography variant="caption" color="text.secondary">{opt.email}</Typography>}
                                                            </Box>
                                                            {opt.already_founder && (
                                                                <Chip label="Already Founder" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                                                            )}
                                                        </Box>
                                                    </li>
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} fullWidth
                                                    label={companyMembers.length > 0 ? 'Select existing member or add new' : 'No members found — add new'}
                                                    placeholder="Choose member..."
                                                />
                                            )}
                                            noOptionsText="No members found"
                                        />
                                    )}
                                    {selectedMember?.already_founder && (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            This member is already registered as a founder for this company.
                                        </Alert>
                                    )}
                                </Box>
                            )}

                            {/* STEP 3: Member Details */}
                            {selectedCompany && showFormFields && (
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
                                        {isNewMember ? 'Step 3 — New Member Details' : 'Step 3 — Confirm Details'}
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Stack spacing={2}>
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <TextField fullWidth label="First Name *" value={createForm.first_name}
                                                    onChange={(e) => setCreateForm(p => ({ ...p, first_name: e.target.value }))}
                                                    disabled={memberLocked}
                                                    size="small"
                                                />
                                                <TextField fullWidth label="Last Name *" value={createForm.last_name}
                                                    onChange={(e) => setCreateForm(p => ({ ...p, last_name: e.target.value }))}
                                                    disabled={memberLocked}
                                                    size="small"
                                                />
                                            </Box>
                                            <TextField fullWidth label="Email *" type="email" value={createForm.email}
                                                onChange={(e) => setCreateForm(p => ({ ...p, email: e.target.value }))}
                                                disabled={memberLocked}
                                                size="small"
                                            />
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <TextField fullWidth label="Phone" value={createForm.phone}
                                                    onChange={(e) => setCreateForm(p => ({ ...p, phone: e.target.value }))}
                                                    size="small"
                                                />
                                                <TextField fullWidth label="LinkedIn URL" value={createForm.linkedin_url}
                                                    onChange={(e) => setCreateForm(p => ({ ...p, linkedin_url: e.target.value }))}
                                                    size="small"
                                                />
                                            </Box>
                                            <TextField fullWidth label="Bio" multiline rows={2} value={createForm.bio}
                                                onChange={(e) => setCreateForm(p => ({ ...p, bio: e.target.value }))}
                                                size="small"
                                            />
                                            <Autocomplete
                                                multiple
                                                options={deals}
                                                getOptionLabel={(opt) => `${opt.title} (${opt.deal_code})` || ''}
                                                value={selectedDeals}
                                                loading={dealLoading}
                                                onOpen={() => { if (deals.length === 0) fetchDeals(); }}
                                                onInputChange={(e, val, reason) => {
                                                    if (reason === 'input') debouncedFetchDeals(val);
                                                }}
                                                onChange={(e, val) => {
                                                    setSelectedDeals(val);
                                                    setCreateForm(p => ({ ...p, deal_ids: val.map(d => d.id) }));
                                                }}
                                                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                                renderInput={(params) => (
                                                    <TextField {...params} fullWidth label="Link to Deals"
                                                        size="small"
                                                        InputProps={{ ...params.InputProps, endAdornment: (
                                                            <>{dealLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>
                                                        )}}
                                                    />
                                                )}
                                                filterOptions={(x) => x}
                                                noOptionsText="Type to search..."
                                            />
                                            {isNewMember && (
                                                <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                                                    This person will also be added as a company member automatically.
                                                </Alert>
                                            )}
                                        </Stack>
                                    </Paper>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button variant="outlined" onClick={() => setCreateOpen(false)}>Cancel</Button>
                        <LoadingButton
                            variant="contained" loading={creating} onClick={handleCreate}
                            disabled={!createForm.company_id || !createForm.first_name || !createForm.email || (!selectedMember && !isNewMember)}
                            sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d1642' } }}
                        >
                            Create Founder
                        </LoadingButton>
                    </Box>
                </Box>
            </Drawer>

            {/* Detail Drawer */}
            <Drawer anchor="right" open={detailOpen} onClose={() => setDetailOpen(false)}
                PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight={700}>Founder Details</Typography>
                        <IconButton onClick={() => setDetailOpen(false)}><Icon icon={closeFill} width={24} /></IconButton>
                    </Box>
                    {selectedFounder && (
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                            <Grid container spacing={2}>
                                {[
                                    ['Name', `${selectedFounder.first_name} ${selectedFounder.last_name}`],
                                    ['Email', selectedFounder.email],
                                    ['Company', selectedFounder.company_name],
                                    ['Status', selectedFounder.status?.replace(/_/g, ' ')],
                                    ['Phone', selectedFounder.phone || '—'],
                                    ['LinkedIn', selectedFounder.linkedin_url || '—'],
                                    ['2FA', selectedFounder.totp_enabled ? 'Enabled' : 'Disabled'],
                                    ['Created', selectedFounder.created ? new Date(selectedFounder.created).toLocaleString() : '—'],
                                ].map(([label, value]) => (
                                    <Grid item xs={6} key={label}>
                                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>{value}</Typography>
                                    </Grid>
                                ))}
                            </Grid>

                            {selectedFounder.bio && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Bio</Typography>
                                    <Typography variant="body2">{selectedFounder.bio}</Typography>
                                </Box>
                            )}

                            {selectedFounder.deals?.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Linked Deals</Typography>
                                    {selectedFounder.deals.map((d) => (
                                        <Chip key={d.id} label={`${d.title} (${d.deal_code})`}
                                            size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}
                    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        {selectedFounder?.totp_enabled && (
                            <LoadingButton
                                variant="outlined" color="warning" loading={resetting2FA}
                                onClick={handleReset2FA}
                                startIcon={<Icon icon={refreshFill} />}
                            >
                                Reset 2FA
                            </LoadingButton>
                        )}
                        <Button variant="outlined" onClick={() => setDetailOpen(false)}>Close</Button>
                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
}
